import {AfterViewInit, Component, EventEmitter, forwardRef, Input, OnInit, Output, OnChanges, SimpleChanges} from '@angular/core';
import {AbstractControl, NG_VALUE_ACCESSOR, FormGroup, FormArray, FormBuilder, FormControl, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {delay, distinctUntilChanged, tap} from 'rxjs/operators';
import {
  compareMatch,
  fromIndividualFormValue,
  IndividualFormValue,
  NotNullable,
  PersonalAssetsValue,
  PersonalLiabilitiesValue,
  toIndividualFormValue,
} from '@portal-workspace/grow-shared-library';
import {UntilDestroy} from '@ngneat/until-destroy';
import {setupUntilDestroy, formControlErrorKeys, formControlErrorMessage} from '@portal-workspace/grow-ui-library';
import {Subject, Subscription} from 'rxjs';
import { PersonalAssetComponentEvent, PersonalAssetsComponent } from './personal-assets.component';
import { PersonalLiabilitiesComponentEvent, PersonalLiabilitiesComponent } from './personal-liabilities.component';
import {AbstractControlValueAccessor} from '../abstract-control-value-accessor';
import {MARK, Mark} from '@portal-workspace/grow-ui-library/mark';
import { DirectorValue ,
  IndividualDirector,
} from '@portal-workspace/grow-shared-library';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MarkDirective } from '../../directives/mark-as-dirty.directive';
import { IndividualFormComponent } from '../individual-form-component/individual-form.component';

import { MessageBoxComponent } from '../message-box/message-box.component';
export interface DirectorComponentEvent {
  valid: boolean,
  entries: DirectorValue;
}

export type DirectorsFormArray = FormArray<DirectorFormGroup>;
export type DirectorFormGroup = FormGroup<{
  kind: FormControl<'Director' | null>,
  type: FormControl<'WithPropertyAddress' | 'WithoutPropertyAddress' | null>,
  individual: FormControl<IndividualFormValue>,
  personalAssets: FormControl<PersonalAssetsValue | null>,
  personalLiabilities: FormControl<PersonalLiabilitiesValue | null>,
}>

export type PossibleDirectorFormGroupControls =
  FormControl<'Director' | null> |
  FormControl<'WithPropertyAddress' | 'WithoutPropertyAddress' | null> |
  FormControl<IndividualFormValue> |
  FormControl<PersonalAssetsValue | null> |
  FormControl<PersonalLiabilitiesValue | null>;

@UntilDestroy({arrayName: 'subscriptions'})
@Component({
    selector: 'director',
    templateUrl: './director.component.html',
    styleUrls: ['./director.component.scss'],
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => DirectorComponent), multi: true },
        { provide: MARK, useExisting: forwardRef(() => DirectorComponent) },
    ],
    standalone: true,
    imports: [FormsModule, ReactiveFormsModule, MarkDirective, IndividualFormComponent, PersonalAssetsComponent, PersonalLiabilitiesComponent, MatButtonModule, MatFormFieldModule, MessageBoxComponent]
})
export class DirectorComponent extends AbstractControlValueAccessor<DirectorValue> implements OnInit, AfterViewInit, OnChanges, Mark {


  errorKeys = formControlErrorKeys;
  errorMessage = formControlErrorMessage;
  subscriptions: Subscription[] = [];
  markObservable: Subject<boolean> = new Subject<boolean>();

  formGroup: FormGroup<{
    directors: DirectorsFormArray,
  }>;
  formArray: DirectorsFormArray;

  @Input({required: false}) withPropertyOwnerAddress = true;
  @Input({required: false}) showAssetsAndLiabilities = true;
  @Input({required: false}) checkDuplicateEmailValidator = true;
  @Input({required: false}) showAddressForm = true;
  @Input({required: false}) showIdentification = false;
  @Input({required: false}) minimumDirectors: number = 1;
  @Output() events: EventEmitter<DirectorComponentEvent> = new EventEmitter<DirectorComponentEvent>();

  subscription?: Subscription;

  constructor(private formBuilder: FormBuilder) {
    super();
    this.formArray = formBuilder.array<DirectorFormGroup>([]);
    this.formGroup = formBuilder.group({
      directors: this.formArray,
    });
  }



  ngOnInit(): void {
    setupUntilDestroy(this);

    this.subscription = this.formArray.valueChanges.pipe(
      delay(0),
      distinctUntilChanged(compareMatch),
      tap(array => {
        let directorValue = null;
        if (this.formArray.invalid) {
          this.propagateChange(null);
        } else {
          directorValue = this.formArrayToDirectorValue(this.formArray);
          this.propagateChange(directorValue);
        }
        this.events.emit({valid: this.formArray.valid, entries: directorValue});
      })
    ).subscribe();

    this.formArray.setValidators([
      this.duplicateDirectorEmailValidator(),
      this.minimumDirectorsValidator()
    ]);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['minimumDirectors'] && !changes['minimumDirectors'].firstChange) {
      this.formArray.updateValueAndValidity();
    }
  }

  ngAfterViewInit(): void {
    if (this.formArray.length === 0) {
      setTimeout(() => {
        this.addDirector();
      });
    }
  }

  directorFormGroups(): DirectorFormGroup[] {
    return this.formArray.controls;
  }

  directorFormGroupFormControl(formGroup: FormGroup, name: string): PossibleDirectorFormGroupControls {
    return (formGroup.controls[name] as PossibleDirectorFormGroupControls);
  }



  duplicateDirectorEmailValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const fa = control as DirectorsFormArray;

      // all the director form groups that has duplicated email
      const directorsFormGroups = fa.controls.filter((g) => {
        const email = g.controls.individual.value?.email;

        const allEmails = fa.controls.map((fg) => fg.controls.individual.value?.email ?? '').filter((email) => !!email);

        const filteredEmails = allEmails.filter(directorEmail => directorEmail === email);
        if (email && filteredEmails.length > 1) {
          return true;
        } else {
          return false;
        }
      });

      if (directorsFormGroups && directorsFormGroups.length > 0) {
        return {
          duplicateDirectorEmail: {
            details: directorsFormGroups
              .map(fg => {
                const individual = fg.controls.individual.value;
                return `${individual?.email ?? ''} ${individual?.firstName ?? ''} ${individual?.lastName ?? ''}`;
              })
          }
        }
      }
      return null;
    }
  }

  minimumDirectorsValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const fa = control as FormArray;

      if (fa.length < this.minimumDirectors) {
        return {
          minimumDirectors: {
            required: this.minimumDirectors,
            actual: fa.length
          }
        };
      }
      return null;
    };
  }



  addDirector(d?: Exclude<DirectorValue, null>[number]) {

    const formControlKind = this.formBuilder.control<'Director' | null>('Director');
    const formControlType = this.formBuilder.control<'WithPropertyAddress' | 'WithoutPropertyAddress' | null>(this.withPropertyOwnerAddress ? 'WithPropertyAddress' : 'WithoutPropertyAddress');
    const formControlIndividual = this.formBuilder.control<IndividualFormValue>(toIndividualFormValue(d), [Validators.required]);
    const formControlPersonalAssets = this.formBuilder.control<PersonalAssetsValue | null>(d ? d.personalAssets : null);
    const formControlPersonalLiabilities = this.formBuilder.control<PersonalLiabilitiesValue | null>(d ? d.personalLiabilities : null);
    const formGroup: DirectorFormGroup = this.formBuilder.group({
      kind: formControlKind,
      type: formControlType,
      individual: formControlIndividual,
      personalAssets: formControlPersonalAssets,
      personalLiabilities: formControlPersonalLiabilities,
    });
    this.formArray.push(formGroup);

    setTimeout(() => this.events.emit({valid: this.formArray.valid, entries: this.formArrayToDirectorValue(this.formArray)}));
  }

  deleteDirector(directorFormGroup: DirectorFormGroup) {
    const index = this.formArray.controls.findIndex(fg => fg === directorFormGroup);
    if (index !== -1) {
      this.formArray.removeAt(index);
    }
    this.events.emit({valid: this.formArray.valid, entries: this.formArrayToDirectorValue(this.formArray)});
  }

  doWriteValue(v: DirectorValue | null | undefined): void {
    this.formArray.clear();
    if (v) {
      for (const d of v) {
        this.addDirector(d);
      }
    }
  }

  onDeleteDirector($event: MouseEvent, directorFormGroup: DirectorFormGroup) {
    this.deleteDirector(directorFormGroup);
  }

  onAddDirector($event: MouseEvent) {
    this.addDirector();
  }

  onPersonalLiabilitiesEvent($event: PersonalLiabilitiesComponentEvent, directorFormGroup: DirectorFormGroup) {
    const liabilities = $event.entries;
    const formControl = directorFormGroup.controls.personalLiabilities;
    if (liabilities && liabilities.length) {
      formControl.setValidators([Validators.required]);
    } else {
      formControl.clearValidators();
    }
    formControl.updateValueAndValidity();
  }

  onPersonalAssetsEvent($event: PersonalAssetComponentEvent, directorFormGroup: DirectorFormGroup) {
    const assets = $event.entries;
    const formControl = directorFormGroup.controls.personalAssets;
    if (assets && assets.length) {
      formControl.setValidators([Validators.required]);
    } else {
      formControl.clearValidators();
    }
    formControl.updateValueAndValidity();
  }

  mark() {
    this.formGroup.markAllAsTouched();
    this.markObservable.next(true);
  }

  formArrayToDirectorValue(formArray: DirectorsFormArray): NotNullable<DirectorValue> {

      const directorValue = formArray.controls.map(fg => {
        // NOTE: when formArray is valid, individual should not be null, validators added should ensure that
        const individual = fromIndividualFormValue(fg.controls.individual.value)!;
        const v: Exclude<DirectorValue, null>[number] = {
          kind: 'Director',
          type: this.withPropertyOwnerAddress ? 'WithPropertyAddress' : 'WithoutPropertyAddress' as any,
          ...individual,
          personalAssets: fg.controls.personalAssets.value!,
          personalLiabilities: fg.controls.personalLiabilities.value!,
        } as Exclude<DirectorValue, null>[number];
        return v;
      });
      return directorValue;

  }



}


